import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  CircularProgress,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Science as TestingIcon,
  School as ActualIcon,
  PersonOutline,
  Group as GroupOutline,
  Refresh,
  Save as SaveIcon,
} from "@mui/icons-material";
import api from "../../api";
import { toast } from "react-toastify";
import { adminHasPermission } from "../../utils/adminPermissions";


const ExamModeManagement = () => {
  const canManage = adminHasPermission("manage-exam-modes");

  // Tab: 0 = All Users, 1 = Role Settings, 2 = Override Settings
  const [tab, setTab] = useState(0);

  // ── All Users tab ──────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(0);
  const [usersPerPage, setUsersPerPage] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearchInput, setUsersSearchInput] = useState("");
  const [usersSearch, setUsersSearch] = useState(""); // debounced
  const [usersRoleFilter, setUsersRoleFilter] = useState("");
  const [rowEdits, setRowEdits] = useState({});
  const [savingUsers, setSavingUsers] = useState(new Set());

  // ── Override Settings tab ──────────────────────────────────────────────────
  const [settings, setSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsPage, setSettingsPage] = useState(0);
  const [settingsPerPage, setSettingsPerPage] = useState(15);
  const [settingsTotal, setSettingsTotal] = useState(0);

  // ── Shared: roles list ─────────────────────────────────────────────────────
  const [roles, setRoles] = useState([]);

  // ── Role Settings form ─────────────────────────────────────────────────────
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleMode, setRoleMode] = useState("actual");
  const [roleAppliesTo, setRoleAppliesTo] = useState("all");
  const [savingRole, setSavingRole] = useState(false);
  const [roleToggleEnabled, setRoleToggleEnabled] = useState(false);


  // ── Delete dialog ──────────────────────────────────────────────────────────
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  // Debounce users search
  useEffect(() => {
    const t = setTimeout(() => {
      setUsersSearch(usersSearchInput);
      setUsersPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [usersSearchInput]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = { page: usersPage + 1, per_page: usersPerPage };
      if (usersSearch.trim()) params.search = usersSearch.trim();
      if (usersRoleFilter) params.role = usersRoleFilter;
      const res = await api.get("admin/exam-modes/users", { params });
      const data = res.data?.data || {};
      setUsers(data.data || []);
      setUsersTotal(data.total || 0);
      setRowEdits({});
    } catch (err) {
      toast.error(err?.response?.status === 403 ? (err?.response?.data?.message || "You do not have permission to view exam modes.") : "Failed to load users.");
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, usersPerPage, usersSearch, usersRoleFilter]);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const params = { page: settingsPage + 1, per_page: settingsPerPage };
      const res = await api.get("admin/exam-modes", { params });
      const data = res.data?.data || {};
      setSettings(data.data || []);
      setSettingsTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.status === 403 ? (err?.response?.data?.message || "You do not have permission to view exam modes.") : "Failed to load override settings.");
    } finally {
      setSettingsLoading(false);
    }
  }, [settingsPage, settingsPerPage]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get("admin/rbac/roles");
      const list = res.data?.data || res.data || [];
      setRoles(Array.isArray(list) ? list : []);
    } catch {
      // not critical
    }
  }, []);

  useEffect(() => { if (tab === 0) fetchUsers(); }, [tab, fetchUsers]);
  useEffect(() => { if (tab === 2) fetchSettings(); }, [tab, fetchSettings]);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // ── Row edit handlers ──────────────────────────────────────────────────────

  const handleRowModeChange = (userId, field, value) => {
    setRowEdits((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [field]: value },
    }));
  };

  const handleSaveRow = async (userId, userName) => {
    const changes = rowEdits[userId];
    if (!changes || Object.keys(changes).length === 0) return;

    setSavingUsers((prev) => new Set([...prev, userId]));
    try {
      const promises = [];
      if (changes.mock_exam_mode !== undefined) {
        promises.push(
          api.post("admin/exam-modes/user", {
            user_id: userId,
            exam_mode: changes.mock_exam_mode,
            applies_to: "mock_exams",
          })
        );
      }
      if (changes.worksheet_mode !== undefined) {
        promises.push(
          api.post("admin/exam-modes/user", {
            user_id: userId,
            exam_mode: changes.worksheet_mode,
            applies_to: "worksheets",
          })
        );
      }
      if (changes.toggle_enabled !== undefined) {
        promises.push(
          api.post("admin/exam-modes/toggle/user", {
            user_id: userId,
            enabled: !!changes.toggle_enabled,
          })
        );
      }
      await Promise.all(promises);

      setUsers((prev) =>
        prev.map((u) =>
          u.id !== userId
            ? u
            : {
                ...u,
                mock_exam_mode: changes.mock_exam_mode ?? u.mock_exam_mode,
                worksheet_mode: changes.worksheet_mode ?? u.worksheet_mode,
                toggle_enabled: changes.toggle_enabled ?? u.toggle_enabled,
              }
        )
      );
      setRowEdits((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      toast.success(`${userName || "User"} mode updated.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update mode.");
    } finally {
      setSavingUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleSetRoleMode = async () => {
    if (!selectedRoleId) {
      toast.warning("Please select a role.");
      return;
    }
    setSavingRole(true);
    try {
      await api.post("admin/exam-modes/role", {
        role_id: parseInt(selectedRoleId, 10),
        exam_mode: roleMode,
        applies_to: roleAppliesTo,
      });
      const roleName = roles.find((r) => r.id === parseInt(selectedRoleId, 10))?.name || selectedRoleId;
      toast.success(`Role "${roleName}" set to ${roleMode} mode (${roleAppliesTo}).`);
      if (tab === 0) fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to set role mode.");
    } finally {
      setSavingRole(false);
    }
  };

  const handleSetRoleToggle = async () => {
    if (!selectedRoleId) {
      toast.warning("Please select a role.");
      return;
    }

    try {
      await api.post("admin/exam-modes/toggle/role", {
        role_id: parseInt(selectedRoleId, 10),
        enabled: roleToggleEnabled,
      });
      const roleName = roles.find((r) => r.id === parseInt(selectedRoleId, 10))?.name || selectedRoleId;
      toast.success(`Role "${roleName}" practice toggle ${roleToggleEnabled ? "enabled" : "disabled"}.`);
      if (tab === 0) fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to set role toggle.");
    }
  };


  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    try {
      await api.delete(`admin/exam-modes/${deleteDialog.id}`);
      toast.success("Setting removed. User/role reverts to Actual mode.");
      setDeleteDialog({ open: false, id: null });
      fetchSettings();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete setting.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getModeChip = (mode) =>
    mode === "testing" ? (
      <Chip icon={<TestingIcon sx={{ fontSize: 16 }} />} label="Testing" color="warning" size="small" sx={{ fontWeight: 600 }} />
    ) : (
      <Chip icon={<ActualIcon sx={{ fontSize: 16 }} />} label="Actual" color="success" size="small" sx={{ fontWeight: 600 }} />
    );

  const getAppliesToLabel = (val) => {
    if (val === "mock_exams") return "Mock Exams";
    if (val === "worksheets") return "Worksheets";
    return "All";
  };

  const modeSelectSx = (mode) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: mode === "testing" ? "#ed6c02" : "#2e7d32",
      borderWidth: 2,
    },
    "& .MuiSelect-select": {
      color: mode === "testing" ? "#e65100" : "#1b5e20",
      fontWeight: 700,
    },
  });

  const refreshCurrent = () => {
    if (tab === 0) fetchUsers();
    else if (tab === 2) fetchSettings();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Exam Mode Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Toggle users or roles between Testing (practice) and Actual (graded) exam modes.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refreshCurrent}
          disabled={usersLoading || settingsLoading}
        >
          Refresh
        </Button>
      </Box>


      {/* Tabs */}
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="All Users" />
          <Tab label="Role Settings" disabled={!canManage} />
          <Tab label="Override Settings" />
        </Tabs>
        <Divider />

        {/* ── Tab 0: All Users ──────────────────────────────────────────────── */}
        {tab === 0 && (
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by name or email..."
                value={usersSearchInput}
                onChange={(e) => setUsersSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={usersRoleFilter}
                  label="Filter by Role"
                  onChange={(e) => { setUsersRoleFilter(e.target.value); setUsersPage(0); }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {canManage && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  Change mode dropdowns and click Save to apply.
                </Typography>
              )}
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mock Exam Mode</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Worksheet Mode</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Practice Toggle</TableCell>
                    {canManage && <TableCell sx={{ fontWeight: 700 }} align="center">Save</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: canManage ? 7 : 6 }).map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No users found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => {
                      const edits = rowEdits[user.id] || {};
                      const mockMode = edits.mock_exam_mode ?? user.mock_exam_mode ?? "actual";
                      const wsMode = edits.worksheet_mode ?? user.worksheet_mode ?? "actual";
                      const toggleValue = edits.toggle_enabled ?? !!user.toggle_enabled;
                      const isDirty = Object.keys(edits).length > 0;
                      const isSaving = savingUsers.has(user.id);
                      const displayName = user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;

                      return (
                        <TableRow key={user.id} hover sx={isDirty ? { bgcolor: "primary.50" } : {}}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {(user.role_names || []).map((rn) => (
                                <Chip key={rn} label={rn} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={mockMode}
                              onChange={(e) => handleRowModeChange(user.id, "mock_exam_mode", e.target.value)}
                              disabled={!canManage || isSaving}
                              sx={{ minWidth: 155, ...modeSelectSx(mockMode) }}
                            >
                              <MenuItem value="actual">Actual (Graded)</MenuItem>
                              <MenuItem value="testing">Testing (Practice)</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={wsMode}
                              onChange={(e) => handleRowModeChange(user.id, "worksheet_mode", e.target.value)}
                              disabled={!canManage || isSaving}
                              sx={{ minWidth: 155, ...modeSelectSx(wsMode) }}
                            >
                              <MenuItem value="actual">Actual (Graded)</MenuItem>
                              <MenuItem value="testing">Testing (Practice)</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Switch
                                checked={toggleValue}
                                onChange={(e) => handleRowModeChange(user.id, "toggle_enabled", e.target.checked)}
                                disabled={!canManage || isSaving}
                              />
                              <Chip
                                size="small"
                                color={toggleValue ? "info" : "default"}
                                label={toggleValue ? "Enabled" : "Disabled"}
                              />
                            </Box>
                          </TableCell>
                          {canManage && (
                            <TableCell align="center">
                              <Tooltip title={isDirty ? "Save changes" : "No unsaved changes"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleSaveRow(user.id, displayName)}
                                    disabled={!isDirty || isSaving}
                                  >
                                    {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={usersTotal}
              page={usersPage}
              onPageChange={(_, p) => setUsersPage(p)}
              rowsPerPage={usersPerPage}
              onRowsPerPageChange={(e) => { setUsersPerPage(parseInt(e.target.value, 10)); setUsersPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </CardContent>
        )}

        {/* ── Tab 1: Role Settings ──────────────────────────────────────────── */}
        {tab === 1 && (
          <CardContent>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Set all users with a specific role to Testing or Actual mode. Individual user-level overrides take priority.
            </Alert>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Role</InputLabel>
                <Select value={selectedRoleId} label="Role" onChange={(e) => setSelectedRoleId(e.target.value)}>
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Mode</InputLabel>
                <Select value={roleMode} label="Mode" onChange={(e) => setRoleMode(e.target.value)}>
                  <MenuItem value="actual">Actual (Graded)</MenuItem>
                  <MenuItem value="testing">Testing (Practice)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Applies To</InputLabel>
                <Select value={roleAppliesTo} label="Applies To" onChange={(e) => setRoleAppliesTo(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="mock_exams">Mock Exams</MenuItem>
                  <MenuItem value="worksheets">Worksheets</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleSetRoleMode}
                disabled={savingRole || !selectedRoleId}
                sx={{ fontWeight: 700, height: 40 }}
              >
                {savingRole ? "Saving..." : "Set Mode"}
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, md: 1 } }}>
                <Typography variant="body2" color="text.secondary">Practice Toggle</Typography>
                <Switch
                  checked={roleToggleEnabled}
                  onChange={(e) => setRoleToggleEnabled(e.target.checked)}
                />
                <Button
                  variant="outlined"
                  onClick={handleSetRoleToggle}
                  disabled={!selectedRoleId}
                  sx={{ fontWeight: 700, height: 40 }}
                >
                  Set Toggle
                </Button>
              </Box>
            </Box>
          </CardContent>
        )}

        {/* ── Tab 2: Override Settings ──────────────────────────────────────── */}
        {tab === 2 && (
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Explicit mode overrides in effect. Users and roles not listed here default to <strong>Actual</strong> mode.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name / Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Set By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                    {canManage && <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settingsLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: canManage ? 8 : 7 }).map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : settings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No overrides. All users default to Actual mode.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    settings.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.id}</TableCell>
                        <TableCell>
                          <Chip
                            icon={s.settable_type?.includes("User")
                              ? <PersonOutline sx={{ fontSize: 14 }} />
                              : <GroupOutline sx={{ fontSize: 14 }} />}
                            label={s.settable_type?.includes("User") ? "User" : "Role"}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {s.settable_name || `ID: ${s.settable_id}`}
                          </Typography>
                          {s.settable_email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {s.settable_email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{getAppliesToLabel(s.applies_to)}</TableCell>
                        <TableCell>{getModeChip(s.exam_mode)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {s.set_by_admin
                              ? `${s.set_by_admin.first_name || ""} ${s.set_by_admin.last_name || ""}`.trim() || `#${s.set_by}`
                              : s.set_by ? `#${s.set_by}` : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {s.updated_at ? new Date(s.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                          </Typography>
                        </TableCell>
                        {canManage && (
                          <TableCell align="center">
                            <Tooltip title="Remove setting (revert to default)">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, id: s.id })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={settingsTotal}
              page={settingsPage}
              onPageChange={(_, p) => setSettingsPage(p)}
              rowsPerPage={settingsPerPage}
              onRowsPerPageChange={(e) => { setSettingsPerPage(parseInt(e.target.value, 10)); setSettingsPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
            />
          </CardContent>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove Exam Mode Setting</DialogTitle>
        <DialogContent>
          <Typography>
            Remove this override? The user/role will revert to <strong>Actual</strong> mode.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamModeManagement;
